import {
  useCallback, useMemo, useRef, useState,
} from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { getFileMetadata, uploadToDirectUrl } from 'Pods/ThreeDStudio/Hooks/directUpload'
import useRequireLogin from 'Pods/ThreeDStudio/Hooks/useRequireLogin'
import useSourceUrlImageUpload from 'Pods/ThreeDStudio/Hooks/useSourceUrlImageUpload'
import {
  formatMaxFileSizeExceededMessage,
} from 'Pods/ThreeDStudio/Shared/Utils/formatMaxFileSizeExceededMessage'
import { getInputFileUploadConstraints } from 'Pods/ThreeDStudio/Shared/Utils/getInputFileUploadConstraints'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openUploadedImageInEditTab, setIsImageUploading } from 'Pods/ThreeDStudio/Store/actions/ui'
import { ACTION } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

import { formatInvalidImageTypeMessage } from './formatInvalidImageTypeMessage'
import { resizeImageToRequirements } from './imageResize'

export default function useImageUploader(setInputImage, storeSchemaKey, sliceName, options = EMPTY_OBJECT) {
  const inputRef = useRef(null)
  // isImageUploading is page-wide (it also disables Generate); isUploading is this
  // instance only, so a multi-view slot can show its own spinner.
  const [isUploading, setIsUploading] = useState(false)
  const modelSchema = useThreeDStudioStore(state => state.schema[storeSchemaKey])
  const isImageUploading = useThreeDStudioStore(state => state.ui.isImageUploading)
  const { postData } = useApiPost()
  // Uploading an image requires an account; prompt a logged-out user to sign up.
  const requireLogin = useRequireLogin()

  const { allowedTypes, maxFileSize } = useMemo(
    () => getInputFileUploadConstraints(modelSchema, 'input_image'),
    [modelSchema],
  )
  const isRetexture = modelSchema?.action === ACTION.RETEXTURE
  // Only the Generate 3D Model uploader feeds the ideation flow, so the retexture one
  // (textureSettings) must not push its source image onto the Edit tab. The extra
  // multi-view slots opt out too: only the front view is the editable reference.
  const feedsIdeationFlow = options.feedsIdeationFlow ?? sliceName === 'modelSettings'

  const applyInputImage = useCallback((id, url) => {
    setInputImage(id, url)
    if (feedsIdeationFlow && id !== undefined) {
      openUploadedImageInEditTab({ id, url })
    }
  }, [setInputImage, feedsIdeationFlow])

  const processFile = useCallback((file) => {
    if (!file || !allowedTypes.includes(file.type)) {
      window.showNotification(
        formatInvalidImageTypeMessage(allowedTypes),
        { type: 'error', autohide: true },
      )

      return Promise.resolve(false)
    }

    if (file.size > maxFileSize) {
      window.showNotification(
        formatMaxFileSizeExceededMessage(maxFileSize),
        { type: 'error', autohide: true },
      )

      return Promise.resolve(false)
    }

    setIsImageUploading(true)
    setIsUploading(true)

    const createGeneratorAsset = (blob) =>
      new Promise((resolve, reject) => {
        postData({
          path: '/api/internal/generator/assets',
          data: { asset_type: 'image', blob },
          onSuccess: resolve,
          onError: reject,
        })
      })

    return resizeImageToRequirements(file, { forcePng: isRetexture })
      .then(resizedFile => {
        return getFileMetadata(resizedFile)
        .then(blob => createGeneratorAsset(blob))
          .then(async response => {
            const { id, direct_upload: directUpload } = response.data
            if (!directUpload?.url || !directUpload?.headers) {
              throw new Error('Invalid direct upload response')
            }
            await uploadToDirectUrl(directUpload.url, directUpload.headers, resizedFile)
            return { id, resizedFile }
          })
      })
      .then(({ id, resizedFile }) => {
        const newBlobUrl = URL.createObjectURL(resizedFile)
        applyInputImage(id, newBlobUrl)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
        return true
      })
      .catch(() => {
        window.showNotification('Upload failed. Please try again.', { type: 'error', autohide: true })
        return false
      })
      .finally(() => {
        setIsImageUploading(false)
        setIsUploading(false)
      })
  }, [postData, allowedTypes, maxFileSize, isRetexture, applyInputImage])

  const onDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const onDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!requireLogin()) {
      return
    }
    const file = e.dataTransfer?.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile, requireLogin])

  const onFileChange = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.target?.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile])

  const onFileClick = useCallback(() => {
    if (!requireLogin()) {
      return
    }
    if (inputRef.current) {
      inputRef.current.click()
    }
  }, [requireLogin])

  const handleRemovePreview = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setInputImage(undefined)
  }, [])

  const handlePasteUrlUpload = useSourceUrlImageUpload(applyInputImage)

  return {
    inputRef,
    allowedTypes,
    isLoading: isImageUploading,
    isUploading,
    onDragOver,
    onDrop,
    onFileChange,
    onFileClick,
    handleRemovePreview,
    handlePasteUrlUpload,
  }
}
